// Tipos del JSON "esquema v1" que consume el generador de la familia de PDF.
// Fuente (solo lectura, no se toca): /Users/diegoperalta/Desktop/Solarix/Propuesta v2/plantilla/lib/nucleo/esquema/propuesta-v1.schema.json
// y ESPEC-FORMATOS §3 (JSON propio de cada formato), §4 (variantes) y §5.5 (común). El GENERADOR valida (N1) y truena;
// estos tipos solo ayudan a que el mapeo compile. MANDA EL ESQUEMA DE LA PLANTILLA: si su forma difiere de estos tipos,
// se corrige el tipo aquí (arquitecto/integrador) y el puente, nunca al revés.
//
// Contrato v2 (10-oct-2026): todo lo nuevo es OPCIONAL y PROVISIONAL (nombres de la ESPEC) hasta que la familia publique
// el subesquema de cada formato y variante. Reglas para el puente (paquetes/pdf):
//  - opciones[].fuente.tipo: el esquema congelado (fase 0) solo admite "sunwise" | "hoja_solarix" | "ejemplo"; el
//    cotizador usa "hoja_solarix" + folio y pasa a "cotizador" SOLO cuando DisponibilidadPdf.capacidades.fuente_cotizador
//    (medido en el esquema de la plantilla) sea true.
//  - periodos[].origen admite "recibo" | "proyectado": recibo, historial_recibo y manual → "recibo"; estimado → "proyectado".
//  - periodos[].{dias, kwh, demanda_kw, cuotas, cargos}: solo con capacidades.periodo_extendido (si no, la plantilla
//    congelada truena por campo de más).
//  - FINSUS (ADENDA A0): el generador calcula la mensualidad de referencia; el puente manda `finsus: null` y
//    formas_de_pago.finsus. FinsusV1 queda como LEGADO (cotizaciones copiadas de su PDF, ya no se usan).
//  - Variantes: solo las que el generador tenga construidas (DisponibilidadPdf.variantes); "ampliacion" aún no existe.
//  - NINGÚN derivado (ahorro, cobertura, retorno, MSI, veredicto, a25…: DERIVADOS_V1) puede ir en el JSON.
//  - privado: el cotizador manda { no_publicar: [...] } con titular, número de servicio, dirección, etc.
import type { AmpliacionBateriasV1 } from './dominio/ampliacion-baterias.ts';
import type { Etapa, Facturacion, FormatoFamilia, NivelRespaldo, OpcionId, Variante } from './dominio/basicos.ts';

/** Contrato v2: valor de opciones[].fuente.tipo para lo que sale del cotizador (cuando la plantilla lo acepte). */
export const FUENTE_TIPO_COTIZADOR = 'cotizador' as const;

// ───────────────────────────── Recibo y periodos ─────────────────────────────

/** ESPEC 3.3: "Tu recibo por renglón", copiado del recibo (pesos sin IVA). Obligatorio en GDMTO/GDMTH (N9). */
export interface CargosRenglonV1 {
  fijo: number;
  energia: number;
  capacidad: number;
  distribucion: number;
}
/** ESPEC 3.3/3.5: kWh por horario (GDMTH). */
export interface KwhHorarioV1 {
  base: number;
  intermedia: number;
  punta: number;
}
/** ESPEC 3.3/3.5: demanda en kW (GDMTO: solo la máxima; no mide punta). */
export interface DemandaV1 {
  punta?: number;
  maxima: number;
}
/** ESPEC 3.5 (horaria): cuotas de CFE del periodo. */
export interface CuotasV1 {
  fijo: number;
  base: number;
  intermedia: number;
  punta: number;
  capacidad: number;
  distribucion: number;
}

export interface PeriodoV1 {
  inicio: string;
  fin: string;
  consumo_kwh: number;
  pago_hoy: number;
  origen?: 'recibo' | 'proyectado';
  /** 4b (D5): «estimado» = el consumo es real pero el pago de ese periodo lo estimó el cotizador con las cuotas de CFE (1 o 2
   *  recibos con su historial). Solo con capacidades.pago_origen y solo cuando es estimado (12 recibos: el JSON de siempre). */
  pago_origen?: 'recibo' | 'estimado';
  cargos?: CargosRenglonV1 | null;
  dias?: number;
  kwh?: KwhHorarioV1;
  demanda_kw?: DemandaV1;
  cuotas?: CuotasV1;
}

/** Un medidor de la variante dos_medidores: sus periodos TAL COMO SE LEYERON (con sus fechas). */
export interface MedidorV1 {
  /** Lo que ve el cliente en la leyenda de la gráfica ("Casa", "Taller"): 2 a 24 letras, sin números. */
  etiqueta: string;
  tarifa: string;
  periodos: PeriodoV1[];
}

/** Medido en el esquema de la plantilla (10-oct-2026): `periodos` O `medidores` (dos medidores: SIN periodos; la
 *  variante suma mes por mes y truena si recibe los periodos ya sumados). */
export interface ReciboV1 {
  tarifa: string;
  region?: 'noroeste';
  facturacion: Facturacion;
  fuente_pago_hoy: 'recibo' | 'cotizador' | 'proyeccion';
  /** IVA que CFE cobra en el recibo de la ciudad (8 en la frontera, 16 en el resto). Solo con capacidades.recibo_iva (L2, 27-oct). */
  iva_pct?: number;
  periodos?: PeriodoV1[];
  /** Variante dos_medidores (ESPEC 4.1): el primero es el medidor que se queda (su tarifa = `tarifa`). */
  medidores?: [MedidorV1, MedidorV1];
  /** Variante dos_medidores: tarifa que queda al unir (DAC si 1F pasa de 2,500 kWh/mes; GDBT si PDBT pasa de 25 kW).
   *  Obligatoria si los medidores tienen tarifas distintas o si juntos cruzan uno de esos umbrales. */
  tarifa_resultante?: string;
  /** ADENDA z27-4 + z28-3 (25-oct, 6c): casa que YA pasa el límite DAC de su tarifa y con la recomendada queda debajo (del
   *  motor: ResultadoMotor.riesgo_dac). Solo formato residencial y solo con capacidades.riesgo_dac. */
  riesgo_dac?: { pago_dac_mes: number; limite_kwh_mes: number };
}

// ───────────────────────────── Opciones solares ─────────────────────────────

/** LEGADO (antes de la ADENDA A0): FINSUS copiado de su PDF. Desde A0 el cotizador manda `finsus: null`. */
export interface FinsusV1 {
  fuente: 'pdf_finsus';
  precio_base: number;
  enganche: number;
  mensualidad: number;
  plazo_meses: 60;
  cat_pct?: number;
  cat_fecha: string;
  folio_finsus?: string;
}

/** ESPEC 5.5 / 2.6: arrendamiento puro con ABC Leasing, copiado de su PDF (solo comercial, industrial, horaria y multisitio). */
export interface ArrendamientoV1 {
  arrendadora: 'abc';
  valor_con_iva: number;
  pago_inicial_con_iva: number;
  plazos: { meses: 24 | 36 | 48; renta_sin_iva: number; residual_sin_iva: number }[];
  vigencia_hasta: string;
  folio: string;
}

export interface FuenteOpcionV1 {
  tipo: 'sunwise' | 'hoja_solarix' | 'ejemplo' | typeof FUENTE_TIPO_COTIZADOR;
  folio?: string;
}

export interface PreciosV1 {
  lista: number;
  contado: number;
  lista_sin_iva?: number;
  contado_sin_iva?: number;
}

export interface OpcionV1 {
  /** Promo de contado: precios.contado es el de promo; aquí, el normal. Solo con capacidades.promo. */
  promo?: { contado_normal: number; contado_normal_sin_iva: number };
  id: OpcionId;
  paneles: number;
  panel_w: number;
  panel_marca: string;
  panel_modelo?: string;
  kwp: number;
  inversores: { marca: string; modelo?: string; kw: number; cantidad: number }[];
  generacion_kwh: number[];
  pago_con_sistema: number[];
  queda_en_minimo?: boolean;
  /** Diego, 12-oct noche (punto a), con supuestos.demanda_neta: kWh que CFE facturaría cada periodo con el sistema (medición
   *  neta, año típico). La plantilla revisa con ellos la capacidad y la distribución que CFE cobraría (N9 con medición neta). */
  kwh_facturados?: number[];
  /** ADENDA A0b: paneles cambiados a mano por el vendedor y cobertura fuera de la banda de la opción (la plantilla la imprime real). */
  ajuste_paneles?: { motivo: string } | null;
  precios: PreciosV1;
  descuento_excepcion?: { pct: number; motivo: string } | null;
  /** PRECIO ÚNICO (Diego, 13-oct-2026): comercio o industria de más de $1 M de contado con IVA: lista = contado, sin
   *  descuento de contado ni MSI. Solo con una plantilla que lo declare en $defs.opcion_solar (capacidad precio_unico). */
  precio_unico?: true;
  /** Diego, 14-oct (z16) y 20-oct (z23): casa 1F, la opción «arranque» se imprime como «Mejor retorno» (los paneles con el
   *  menor retorno). Solo con una plantilla que lo declare en $defs.opcion_solar (capacidad mejor_retorno). */
  mejor_retorno?: true;
  /** ADENDA z24 (Diego, 20-oct-2026): la primera opción lleva el inversor de la Exacta y queda lista para crecer hasta los
   *  paneles de la Exacta (su tarjeta dice «Listo para crecer a N paneles»). Solo con una plantilla que lo declare en
   *  $defs.opcion_solar (capacidad listo_para_ampliar); lo del precio escrito es del vendedor y no va al JSON del cliente. */
  inversor_listo_para_ampliar?: { paneles_hasta: number };
  finsus?: FinsusV1 | null;
  arrendamiento?: ArrendamientoV1 | null;
  fuente: FuenteOpcionV1;
}

// ───────────────────────────── Baterías (ESPEC 3.4) y horaria (ESPEC 3.5) ─────────────────────────────

// Medido en formatos/baterias/esquema.json de la plantilla (10-oct-2026). Las garantías NO se teclean (N10: salen de
// garantias.json de la plantilla); ningún derivado (horas, ahorro, MSI, mensualidad…).
export interface BateriaV1 {
  marca: string;
  modelo: string;
  modulos: number;
  /** Del banco completo (todos los módulos). */
  kwh_nominales: number;
  kwh_utiles: number;
  quimica?: 'LFP' | 'NMC';
}
export interface InversorRespaldoV1 {
  marca: string;
  modelo: string;
  /** Potencia continua de UN inversor (kW, de la ficha). */
  kw_continuos: number;
  cantidad?: number;
  /** Obligatorio en "respaldo" e "hibrido"; en "aislado" no va. */
  ms_cambio?: number;
  /** De dónde se copiaron los kW y los milisegundos. */
  ficha: string;
}
export interface CargaRespaldoV1 {
  equipo: string;
  cantidad: number;
  w: number;
  /** Solo "aislado" (y ahí obligatorio). */
  horas_dia?: number;
}
export interface NivelRespaldoV1 {
  id: NivelRespaldo;
  /** Solo con alcance "cargas" (aislado). En "toda_la_casa" no va (11-oct: sin cargas críticas). */
  cargas?: CargaRespaldoV1[];
  fuera?: string[];
  carga_kw: { min: number; max: number };
  bateria: BateriaV1;
  inversor?: InversorRespaldoV1;
  precios: PreciosV1;
  descuento_excepcion?: { pct: number; motivo: string } | null;
  finsus?: FinsusV1 | null;
  arrendamiento?: ArrendamientoV1 | null;
  fuente: FuenteOpcionV1;
}
export interface SolarRespaldoV1 {
  paneles: number;
  panel_w: number;
  panel_marca: string;
  panel_modelo?: string;
  kwp: number;
  generacion_kwh?: number[];
  pago_con_sistema?: number[];
  queda_en_minimo?: boolean;
  /** Solo el híbrido: la opción lleva el mínimo de paneles del híbrido (cobertura hasta 200 %). Con capacidades.minimo_hibrido (L2, 27-oct). */
  minimo_hibrido?: true;
  generacion_kwh_dia?: number;
  /** Solo el híbrido (Diego, 14-oct-2026 22:30, MANDA): contado con IVA de los PANELES solos (el mismo de la opción como «solo
   *  paneles»). La plantilla saca el retorno SOLO de aquí: las baterías son respaldo, sin retorno. Con una plantilla que lo
   *  acepta (capacidades.retorno_paneles). */
  precio_contado?: number;
  /** Solo 'hibrido' acoplado en CA (25-oct, 6c; capacidades.inversor_red_ca): el inversor de red de los paneles, que se cobra. */
  inversores_red?: { marca: string; modelo: string; cantidad: number }[];
}
export interface RespaldoV1 {
  tipo: 'respaldo' | 'hibrido' | 'aislado';
  uso: 'casa' | 'negocio';
  con_paneles: boolean;
  /** Solo tipo "respaldo". */
  tarifa?: string;
  inversor: InversorRespaldoV1;
  /** Diego, 11-oct (MANDA): "toda_la_casa" en respaldo e híbrido (sin tablero ni cargas críticas; niveles por horas con el
   *  consumo del recibo) o "cargas" (solo el aislado). El tablero de respaldo ya no existe. */
  alcance: 'toda_la_casa' | 'cargas';
  solar?: SolarRespaldoV1;
  niveles: NivelRespaldoV1[];
  recomendado: NivelRespaldo;
}

/** Medido en formatos/horaria/esquema.json de la plantilla (-6v; -6x-peak suma lo del recorte de punta, 29-oct-2026). */
export interface EstudioHorarioV1 {
  metodo: 'recibos_gdmth';
  eficiencia_ida_vuelta: number;
  fuente: string;
  /** Recorte de punta (capacidades.recorte_punta, z54): las cuotas con que se cuenta el ahorro todo el año (las del último mes
   *  publicado, con su mes AAAA-MM), el tope del recorte (0.9 × la punta) y los meses fallados del rango ([2, 6]). Los tres juntos. */
  cuotas_ahorro?: CuotasV1 & { mes: string };
  tope_recorte_pct_punta?: number;
  meses_fallados?: number[];
}
/** Una batería de la horaria: su garantía va copiada de la cotización/ficha (la plantilla la cruza con garantias.json, N10). */
export interface BateriaHorariaV1 extends BateriaV1 {
  garantia: { anios: number; ciclos?: number; pct_capacidad_final?: number; fuente: string };
}
export interface OpcionHorariaV1 {
  id: OpcionId;
  bateria: BateriaHorariaV1;
  inversores: { marca: string; modelo: string; kw: number; cantidad: number }[];
  kw_recorte: number;
  kwh_desplazados_por_dia: number;
  dmax_mes_con?: number[] | null;
  precios: PreciosV1;
  precio_unico?: true;
  /** Recorte de punta (plantilla -6y-peak, verificación de hoy70): el precio del equipo sale de una cotización vencida: el PDF dice que
   *  lo confirma el proveedor. */
  precio_por_confirmar?: boolean;
  /** Recorte de punta: kVA del transformador elevador (estimado) que ya va en el precio; null = sin transformador. Con varias
   *  unidades, el total. */
  transformador_kva?: number | null;
  /** Recorte de punta (plantilla -7a-peak): cuántos transformadores suman esos kVA («2 transformadores de 500 kVA»). */
  transformador_unidades?: number | null;
  descuento_excepcion?: { pct: number; motivo: string } | null;
  finsus?: FinsusV1 | null;
  arrendamiento?: ArrendamientoV1 | null;
  fuente: FuenteOpcionV1;
}

// ───────────────────────────── Desglose (3.6), piezas (3.7), multisitio (3.8) ─────────────────────────────

export interface ReferenciaDesgloseV1 {
  folio_propuesta: string;
  opcion: OpcionId;
  total_con_iva: number;
  /** H-5: si los unitarios son a lista o a contado (obligatorio). */
  base: 'contado' | 'lista';
}
export interface GrupoDesgloseV1 {
  nombre: string;
  partidas: { concepto: string; unidad: string; cantidad: number; pu_sin_iva: number }[];
}

/** Partida del formato piezas, como la pide su subesquema (formatos/piezas/esquema.json, -6j): la garantía dice «alcance», no
 *  «cobertura» (el núcleo prohíbe esa clave en todo el JSON: es un derivado de los solares); `equipo` solo en una pieza que es
 *  panel, inversor o batería (para revisar su garantía registrada, N10). */
export interface PartidaServicioV1 {
  concepto: string;
  tipo: 'pieza' | 'trabajo';
  cantidad: number;
  unidad: string;
  pu_sin_iva: number;
  equipo?: { grupo: 'paneles' | 'inversores' | 'baterias'; marca: string; modelo?: string };
  garantia: { quien: 'fabricante' | 'solarix'; meses: number; alcance?: string; como_hacerla_valida?: string };
}
export interface ServicioV1 {
  tipo: 'pieza' | 'reparacion' | 'mantenimiento';
  titulo: string;
  descripcion: string;
  foto?: 'servicio' | 'inversor' | 'paneles';
  partidas: PartidaServicioV1[];
  /** Obligatorio con tipo "reparacion" (NOM-174-SCFI-2007 4.7). La forma exacta la fija el subesquema de piezas. */
  diagnostico: Record<string, unknown> | null;
  entrega_dias_habiles: { min: number; max: number };
  anticipo_pct: number;
  /** Hoja 2 de la ampliación de baterías: SOLO con capacidades.ampliacion_baterias (plantilla -6j-ampliacion o posterior). */
  ampliacion_baterias?: AmpliacionBateriasV1;
  /** Diego, 27-oct-2026 («no pongas desglose de precios, englóbalo todo»): lo que incluye sin P. U. ni importes; abajo subtotal,
   *  IVA, total, anticipo y 3 MSI. La ampliación de baterías lo manda SIEMPRE y solo con capacidades.sin_desglose. */
  sin_desglose?: true;
}

export interface SitioV1 {
  id: string;
  nombre: string;
  grupo?: string;
  ciudad?: string;
  recibo: ReciboV1;
  opciones: OpcionV1[];
}
export interface AlcanceMultisitioV1 {
  finsus?: FinsusV1 | null;
  arrendamiento?: ArrendamientoV1 | null;
}
export interface SitioMultisitioV1 extends SitioV1 {
  tipo_forzado?: { motivo: string };
  /** Medición neta POR SITIO (Diego, 14-oct-2026): con supuestos.demanda_neta en el primer nivel, cada sucursal incluida dice
   *  si SU pago con sistema trae la baja de capacidad y distribución con la fórmula de CFE. Solo con
   *  capacidades.demanda_neta_por_sitio (la plantilla de antes no lo admite). */
  demanda_neta?: boolean;
  /** La Cacharamba (Diego, 26-oct-2026): cuántos de sus periodos son estimados (origen 'proyectado'). La sucursal SÍ entra al
   *  PDF de todas y la plantilla marca esos meses «Estimado». Solo con capacidades.meses_estimados_por_sitio. */
  meses_estimados?: number;
  /** 4a (ADENDA, garantía de PTR, suelo y carport; Diego, 26-oct-2026): la estructura de ESTE sitio cuando no es losa/lámina.
   *  La plantilla quita del PDF de todas los 20 años del fabricante de la estructura en cuanto un sitio no es losa/lámina. Solo
   *  con capacidades.estructura_por_sitio. */
  estructura?: { tipo: 'ptr' | 'suelo' | 'carport' } | null;
}
/** Un sitio que NO entra a la propuesta (va en `excluir` con su motivo; la plantilla lo lista en el anexo). Sin recibo ni
 *  opciones (regla «y» de Diego, 13-oct: «si la plantilla no puede con alguna, sale aparte y se avisa»). */
export interface SitioExcluidoV1 {
  id: string;
  nombre: string;
  ciudad: string;
}
/** Un sitio del «todo en uno» con casas y bimestrales (formato consolidado, ADENDA z68-3): el MISMO recibo y la Exacta de su
 *  propuesta individual; la plantilla lo calcula con el modelo solar común y solo suma. */
export interface SitioConsolidadoV1 {
  id: string;
  nombre: string;
  ciudad: string;
  tipo: 'casa' | 'negocio';
  tipo_forzado?: { motivo: string };
  demanda_neta?: boolean;
  estructura?: PropuestaV1['estructura'];
  recibo: ReciboV1;
  opciones: [OpcionV1];
}

/** Formato consolidado (ADENDA z65-3, z66-2 y z68-3, Diego 29 a 31-oct-2026): 2 a 15 sitios, cada uno en la periodicidad de SU
 *  recibo y al año; el total del proyecto al año. Solo con una plantilla que tenga el formato (si no, el PDF de todas truena). */
export interface ConsolidadoV1 {
  /** Regla 5 de la ADENDA: el paquete pasa del umbral y lleva un solo precio (sin lista ni descuento). Es del paquete: una casa no lleva precio_unico. */
  precio_unico?: true;
  sitios: SitioConsolidadoV1[];
  un_viaje_por_ciudad?: { ciudad: string; sitios: number }[];
  /** Los sitios del proyecto que van en su propia propuesta, con su motivo corto (la hoja 3 los nombra). */
  fuera?: { nombre: string; motivo: string }[];
}

export interface MultisitioV1 {
  /** ADENDA z67-1: las ciudades en las que el PDF de todas cobra UN viaje para varias sucursales (la línea que explica por qué
   *  el total es menor que la suma de los PDF sueltos). Solo con capacidades.un_viaje_por_ciudad. */
  un_viaje_por_ciudad?: { ciudad: string; sitios: number }[];
  sitio_nombre: { singular: string; plural: string; genero: 'f' | 'm' };
  grupos?: { id: string; nombre: string }[];
  sitios: (SitioMultisitioV1 | SitioExcluidoV1)[];
  /** Qué alcances se ofrecen (clave presente = se ofrece); FINSUS y arrendamiento van por alcance, nunca por sitio. */
  alcances: { arranque?: AlcanceMultisitioV1; exacta: AlcanceMultisitioV1; holgada?: AlcanceMultisitioV1 };
  destacados?: Record<string, string>;
  excluir?: Record<string, string>;
}

// ───────────────────────────── Propuesta ─────────────────────────────

export interface PropuestaV1 {
  version_esquema: 1;
  /** Promo de contado (Diego, 2-nov-2026). Solo con capacidades.promo. */
  promo?: { id: string; nombre: string; pct: number; vence: string };
  formato: FormatoFamilia;
  variantes?: Variante[];
  folio: string;
  etapa: Etapa;
  fecha: string;
  vigencia_dias: number;
  asesor_id: string;
  /** ADENDA A0b: vendedor sin tarjeta en la plantilla → su nombre y su WhatsApp (tarjeta sin foto). Solo si asesor_id no está en el registro. */
  asesor_respaldo?: { nombre_en_pdf: string; whatsapp: string; correo: string | null };
  /** OPTIMUS (Diego, 12-oct noche, ADENDA c): el asesor es de Optimus → la tarjeta lleva, chica, «Asesor de Optimus, aliado
   *  comercial de Solarix Energy» (el texto lo pone la plantilla; aquí solo el id). Solo si la plantilla lo acepta
   *  (capacidades.asesor_aliado); si no, no hay PDF. */
  asesor_aliado?: 'optimus';
  /** regimen (16-oct-2026): con él la plantilla elige el producto de FINSUS (persona_moral y pf_empresarial → crédito empresarial). Solo si
   *  la plantilla lo acepta (capacidades.finsus_empresarial). */
  cliente: { nombre: string; ciudad: string; estado?: string; giro?: string; regimen?: 'persona_moral' | 'pf_empresarial' | 'pf_resico' | 'particular'; homonimo_de_asesor?: { motivo: string } };
  /** Adenda A0b: SIEMPRE 16 (el esquema de la plantilla solo admite 16). */
  iva_pct?: 16;
  tipo_forzado?: { motivo: string } | null;
  fotos?: { portada?: string | null };
  privado: { archivo?: string; ninguno?: true; no_publicar?: string[] };
  notas_internas?: string[];
  recibo?: ReciboV1;
  opciones?: OpcionV1[];
  recomendada?: OpcionId;
  formas_de_pago?: {
    contado: true;
    etapas: boolean;
    /** 18: solo con la promo de casa (Diego, 3-nov-2026; mapear.ts). */
    msi: (3 | 6 | 12 | 18)[];
    finsus: boolean;
    arrendamiento: boolean;
    deduccion: boolean;
  };
  /** demanda_neta (Diego, 12-oct noche, punto a): en GDMTO/GDMTH el pago con sistema ya trae la baja de capacidad y
   *  distribución con la fórmula de CFE sobre los kWh netos (cada opción manda kwh_facturados); la propuesta dice "Estimado con
   *  las reglas de CFE para tu tarifa" y "año típico". Solo con capacidades.demanda_neta. */
  supuestos?: {
    demanda_baja?: { fuente: string; confirmado: 'Diego' } | null;
    demanda_neta?: { regla: 'cfe_medicion_neta'; fuente: string; confirmado: 'Diego' } | null;
  };
  /** Diego, 12-oct: régimen y tasa de ISR del cliente que deduce (persona moral o física con actividad empresarial); el
   *  generador calcula el beneficio fiscal y el segundo retorno (no son campos del JSON). Solo con formas_de_pago.deduccion. */
  deduccion_fiscal?: { regimen: 'persona_moral' | 'pf_empresarial'; tasa_isr_pct: number } | null;
  /** Diego, 12-oct: la estructura cuando NO es la de siempre (losa/lámina = ausente). La plantilla la dice en lo que incluye
   *  y, como decidió Diego el 12-oct por la tarde, con PTR, suelo o carport da SOLO la garantía de instalación de Solarix
   *  (3 años), sin los 20 años del fabricante; suelo y carport, además, sin "goteras en los anclajes" ni "tu techo". */
  estructura?: { tipo: 'ptr' | 'suelo' | 'carport' } | null;
  /** Etapa 3 (Diego, 12-oct, punto 3): la generación por kW con que el cotizador calculó TODO lo prometido de esta propuesta
   *  (kWh por kWp al año) y la base de Diego. La plantilla revisa contra ESTA cifra (no contra 1,750 fijo): la generación de
   *  cada opción no puede pasarla, y pasar de la base (o de 1,750) solo vale con `sube_a_sabiendas` y su motivo. Ausente = la
   *  política de siempre (1,750). */
  generacion?: { kwh_kwp_anio: number; base_kwh_kwp_anio: number; sube_a_sabiendas: boolean; motivo: string | null } | null;

  // ---- Variantes (ESPEC §4), medidas en variantes/<id>.mjs de la plantilla (10-oct-2026) ----
  /** base_futura (ADENDA z52, plantilla -6t): el cálculo no es el recibo tal cual. `recibo_real` = lo que dice el recibo (su tarifa,
   *  lo que paga por periodo, sus kWh y cuántos pagos estimó el cotizador porque el recibo no los trae); `cambio_tarifa` = negocio con
   *  recibo de casa: «hoy» y «sin paneles» siguen en la de casa y «con tu sistema» va en PDBT/GDBT con el alumbrado (D3, 28-oct);
   *  `aumento` = kWh de más del aumento de consumo (con él, «pagarías sin paneles» lo calculó el cotizador: fuente «cotizador»). */
  base_futura?: {
    recibo_real: { tarifa: string; pago_por_periodo: number; kwh_anual: number; pagos_estimados: number };
    cambio_tarifa: { de: string; a: 'PDBT' | 'GDBT' } | null;
    aumento: { kwh_anual: number; motivo: string; fuente: 'ia' | 'ia_simulada' | 'reglas' | 'vendedor' } | null;
  };
  /** dos_medidores: si la baja del segundo medidor va incluida (null = por confirmar). Costo y tiempo: H-4, null. */
  unificacion?: { incluida: boolean | null; costo_con_iva: null; tiempo: null };
  /** consumo_proyectado: casas_parecidas solo en residencial; negocios_parecidos en comercial e industrial (GDMTO). */
  estimacion?: {
    metodo: 'cuadro_cargas' | 'casas_parecidas' | 'negocios_parecidos' | 'consumo_cliente';
    /** 1 a 5 (obligatorias con cuadro_cargas): equipo de 2 a 32 letras sin punto final; horas_dia > 0. */
    cargas?: { equipo: string; cantidad: number; w: number; horas_dia: number; meses_uso: number }[];
    rango_pct: number;
    fuente: string;
  };
  /** aumento_consumo: kWh AL AÑO (mayor que la suma de los periodos); motivo de 3 a 40 caracteres sin paréntesis ni
   *  punto final. La Holgada es obligatoria y la recomendada es la Holgada (o no se manda). */
  crecimiento?: { consumo_futuro_kwh_anual: number; motivo: string; fuente: 'cliente' };
  /** historial_incompleto: casas_parecidas (casa) | negocios_parecidos (negocio y planta) | sunwise. */
  proyeccion?: { metodo: 'casas_parecidas' | 'negocios_parecidos' | 'sunwise' | 'ia'; fuente: string };
  /** ampliacion: el sistema que YA tiene (todas las claves; null = no se sabe). En una propuesta final, fuente
   *  "visita_tecnica" y sin null en paneles, panel_w, kwp e inversores. Con "inversor_actual", cada opción repite el
   *  inversor que ya tiene. El recibo va tal cual (NETMET) y las opciones son solo lo nuevo. */
  ampliacion?: {
    existente: {
      paneles: number | null;
      panel_w: number | null;
      kwp: number | null;
      inversores: { marca: string; modelo?: string; kw: number; cantidad: number }[] | null;
      anio_instalacion: number | null;
      instalo: 'solarix' | 'otro' | null;
    };
    conexion: 'inversor_nuevo' | 'inversor_actual';
    fuente: 'cliente' | 'visita_tecnica';
  };

  // ---- Formatos nuevos (ESPEC §3), contrato v2, provisionales ----
  /** baterias. */
  respaldo?: RespaldoV1;
  /** horaria (las opciones de horaria van en PropuestaHorariaV1). */
  perfil_15min?: { archivo: string; fuente: string } | null;
  estudio?: EstudioHorarioV1;
  /** desglose. */
  referencia?: ReferenciaDesgloseV1;
  grupos?: GrupoDesgloseV1[];
  /** piezas. */
  servicio?: ServicioV1;
  /** multisitio. */
  multisitio?: MultisitioV1;
  consolidado?: ConsolidadoV1;
}

/** Horaria: mismas claves comunes, pero sus opciones son baterías (no paneles). */
export type PropuestaHorariaV1 = Omit<PropuestaV1, 'formato' | 'opciones'> & { formato: 'horaria'; opciones: OpcionHorariaV1[] };

/** Cualquier JSON de la familia que el puente puede producir (ResultadoPdf.json_v1 sigue siendo PropuestaV1 hasta que
 *  el puente produzca horaria). */
export type PropuestaFamiliaV1 = PropuestaV1 | PropuestaHorariaV1;

/** Claves prohibidas en cualquier nivel del JSON v1 (copia de DERIVADOS en lib/nucleo/esquema.mjs del generador,
 *  leída el 9-oct-2026). Sirve para la prueba del puente: si el mapeo produce una, la prueba truena antes que el PDF. */
export const DERIVADOS_V1 = Object.freeze([
  'veredicto', 'ahorro', 'ahorro_anual', 'ahorro_mensual', 'ahorro_mes', 'cobertura', 'msi_mensual', 'mensualidad_msi', 'a25',
  'ahorro_25', 'ahorro_25_anios', 'retorno', 'retorno_anios', 'margen', 'margen_kwh', 'diferencia_contado', 'saldo', 'saldo_banco',
  'cubre_pct', 'te_sobran', 'etapas_montos', 'anticipo', 'finiquito',
] as const);

/** Busca claves derivadas en un JSON (devuelve sus rutas). */
export function derivadosPresentes(json: unknown, ruta = 'json'): string[] {
  const hallados: string[] = [];
  if (Array.isArray(json)) json.forEach((x, i) => hallados.push(...derivadosPresentes(x, `${ruta}[${i}]`)));
  else if (json && typeof json === 'object')
    for (const [k, v] of Object.entries(json)) {
      if ((DERIVADOS_V1 as readonly string[]).includes(k)) hallados.push(`${ruta}.${k}`);
      hallados.push(...derivadosPresentes(v, `${ruta}.${k}`));
    }
  return hallados;
}
